/**
 * Question Set CSV Parser and Generator
 * Requirement 49.8: Multi-language CSV import extension
 */

/**
 * Supported languages for multi-language CSV
 */
export const SUPPORTED_LANGUAGES = ['en', 'id', 'mn', 'ne', 'vi'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'English',
  id: 'Bahasa Indonesia',
  mn: 'Монгол хэл',
  ne: 'नेपाली',
  vi: 'Tiếng Việt',
};

/**
 * Multi-language text fields
 */
export interface MultiLanguageText {
  textEn?: string;
  textId?: string;
  textMn?: string;
  textNe?: string;
  textVi?: string;
}

/**
 * Parsed question structure with multi-language support
 */
export interface ParsedQuestion extends MultiLanguageText {
  order: number;
  text: string;
  choiceA: {
    text: string;
    thinkingTypeCode: string;
  } & MultiLanguageText;
  choiceB: {
    text: string;
    thinkingTypeCode: string;
  } & MultiLanguageText;
}

/**
 * CSV parse result
 */
export interface QuestionSetCsvParseResult {
  questionSetTitle: string;
  questions: ParsedQuestion[];
  isMultiLanguage: boolean;
  errors: CsvValidationError[];
  isValid: boolean;
}

/**
 * CSV validation error
 */
export interface CsvValidationError {
  row: number;
  field?: string;
  message: string;
}

/**
 * CSV Headers
 */
const LEGACY_HEADERS = [
  '質問セットタイトル',
  '質問文',
  '選択肢A',
  '選択肢B',
  '選択肢A思考傾向コード',
  '選択肢B思考傾向コード',
];

const MULTI_LANGUAGE_HEADERS = [
  '質問セットタイトル',
  '質問文',
  '質問文(en)',
  '質問文(id)',
  '質問文(mn)',
  '質問文(ne)',
  '質問文(vi)',
  '選択肢A',
  '選択肢A(en)',
  '選択肢A(id)',
  '選択肢A(mn)',
  '選択肢A(ne)',
  '選択肢A(vi)',
  '選択肢B',
  '選択肢B(en)',
  '選択肢B(id)',
  '選択肢B(mn)',
  '選択肢B(ne)',
  '選択肢B(vi)',
  '選択肢A思考傾向コード',
  '選択肢B思考傾向コード',
];

/**
 * Parse a single CSV line handling quoted fields
 */
function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      fields.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current);

  return fields;
}

/**
 * Detect CSV format based on header row
 */
function detectCsvFormat(headerLine: string): 'legacy' | 'multi-language' {
  const headers = parseCSVLine(headerLine);
  if (headers.some((h) => h.includes('(en)') || h.includes('(id)') || h.includes('(mn)'))) {
    return 'multi-language';
  }
  return 'legacy';
}

/**
 * Parse CSV data into question set structure
 */
export function parseQuestionSetCSV(csvData: string): QuestionSetCsvParseResult {
  const lines = csvData
    .trim()
    .split('\n')
    .map((line) => line.replace(/\r$/, ''));
  const errors: CsvValidationError[] = [];

  if (lines.length < 2) {
    return {
      questionSetTitle: '',
      questions: [],
      isMultiLanguage: false,
      errors: [{ row: 0, message: 'CSVには少なくともヘッダー行と1行のデータが必要です' }],
      isValid: false,
    };
  }

  const format = detectCsvFormat(lines[0]);
  const isMultiLanguage = format === 'multi-language';
  const expectedColumns = isMultiLanguage ? 21 : 6;

  // Validate header
  const headers = parseCSVLine(lines[0]);
  const expectedHeaders = isMultiLanguage ? MULTI_LANGUAGE_HEADERS : LEGACY_HEADERS;

  if (headers.length < expectedColumns) {
    errors.push({
      row: 1,
      message: `ヘッダー列数が不足しています: ${headers.length} (期待値: ${expectedColumns})`,
    });
  }

  const dataLines = lines.slice(1);
  const questions: ParsedQuestion[] = [];
  let questionSetTitle = '';

  for (let i = 0; i < dataLines.length; i++) {
    const line = dataLines[i].trim();
    if (!line) continue;

    const fields = parseCSVLine(line);
    const rowNum = i + 2; // 1-indexed, accounting for header

    if (fields.length < expectedColumns) {
      errors.push({
        row: rowNum,
        message: `列数が不足しています: ${fields.length} (期待値: ${expectedColumns})`,
      });
      continue;
    }

    if (isMultiLanguage) {
      const [
        setTitle,
        questionText,
        questionTextEn,
        questionTextId,
        questionTextMn,
        questionTextNe,
        questionTextVi,
        choiceAText,
        choiceATextEn,
        choiceATextId,
        choiceATextMn,
        choiceATextNe,
        choiceATextVi,
        choiceBText,
        choiceBTextEn,
        choiceBTextId,
        choiceBTextMn,
        choiceBTextNe,
        choiceBTextVi,
        choiceAType,
        choiceBType,
      ] = fields;

      // Use the first row's question set title
      if (!questionSetTitle && setTitle) {
        questionSetTitle = setTitle.trim();
      }

      // Validate required fields
      if (!questionText?.trim()) {
        errors.push({ row: rowNum, field: '質問文', message: '質問文は必須です' });
        continue;
      }
      if (!choiceAText?.trim()) {
        errors.push({ row: rowNum, field: '選択肢A', message: '選択肢Aは必須です' });
        continue;
      }
      if (!choiceBText?.trim()) {
        errors.push({ row: rowNum, field: '選択肢B', message: '選択肢Bは必須です' });
        continue;
      }
      if (!choiceAType?.trim()) {
        errors.push({
          row: rowNum,
          field: '選択肢A思考傾向コード',
          message: '選択肢A思考傾向コードは必須です',
        });
        continue;
      }
      if (!choiceBType?.trim()) {
        errors.push({
          row: rowNum,
          field: '選択肢B思考傾向コード',
          message: '選択肢B思考傾向コードは必須です',
        });
        continue;
      }

      // Validate character limits (200 bytes for multi-language support)
      if (choiceAText.trim().length > 200) {
        errors.push({
          row: rowNum,
          field: '選択肢A',
          message: '選択肢Aは200文字以内で入力してください',
        });
      }
      if (choiceBText.trim().length > 200) {
        errors.push({
          row: rowNum,
          field: '選択肢B',
          message: '選択肢Bは200文字以内で入力してください',
        });
      }

      questions.push({
        order: questions.length + 1,
        text: questionText.trim(),
        textEn: questionTextEn?.trim() || undefined,
        textId: questionTextId?.trim() || undefined,
        textMn: questionTextMn?.trim() || undefined,
        textNe: questionTextNe?.trim() || undefined,
        textVi: questionTextVi?.trim() || undefined,
        choiceA: {
          text: choiceAText.trim(),
          thinkingTypeCode: choiceAType.trim(),
          textEn: choiceATextEn?.trim() || undefined,
          textId: choiceATextId?.trim() || undefined,
          textMn: choiceATextMn?.trim() || undefined,
          textNe: choiceATextNe?.trim() || undefined,
          textVi: choiceATextVi?.trim() || undefined,
        },
        choiceB: {
          text: choiceBText.trim(),
          thinkingTypeCode: choiceBType.trim(),
          textEn: choiceBTextEn?.trim() || undefined,
          textId: choiceBTextId?.trim() || undefined,
          textMn: choiceBTextMn?.trim() || undefined,
          textNe: choiceBTextNe?.trim() || undefined,
          textVi: choiceBTextVi?.trim() || undefined,
        },
      });
    } else {
      // Legacy format
      const [setTitle, questionText, choiceAText, choiceBText, choiceAType, choiceBType] = fields;

      if (!questionSetTitle && setTitle) {
        questionSetTitle = setTitle.trim();
      }

      // Validate required fields
      if (!questionText?.trim()) {
        errors.push({ row: rowNum, field: '質問文', message: '質問文は必須です' });
        continue;
      }
      if (!choiceAText?.trim()) {
        errors.push({ row: rowNum, field: '選択肢A', message: '選択肢Aは必須です' });
        continue;
      }
      if (!choiceBText?.trim()) {
        errors.push({ row: rowNum, field: '選択肢B', message: '選択肢Bは必須です' });
        continue;
      }
      if (!choiceAType?.trim()) {
        errors.push({
          row: rowNum,
          field: '選択肢A思考傾向コード',
          message: '選択肢A思考傾向コードは必須です',
        });
        continue;
      }
      if (!choiceBType?.trim()) {
        errors.push({
          row: rowNum,
          field: '選択肢B思考傾向コード',
          message: '選択肢B思考傾向コードは必須です',
        });
        continue;
      }

      // Validate character limits (200 bytes for multi-language support)
      if (choiceAText.trim().length > 200) {
        errors.push({
          row: rowNum,
          field: '選択肢A',
          message: '選択肢Aは200文字以内で入力してください',
        });
      }
      if (choiceBText.trim().length > 200) {
        errors.push({
          row: rowNum,
          field: '選択肢B',
          message: '選択肢Bは200文字以内で入力してください',
        });
      }

      questions.push({
        order: questions.length + 1,
        text: questionText.trim(),
        choiceA: {
          text: choiceAText.trim(),
          thinkingTypeCode: choiceAType.trim(),
        },
        choiceB: {
          text: choiceBText.trim(),
          thinkingTypeCode: choiceBType.trim(),
        },
      });
    }
  }

  return {
    questionSetTitle,
    questions,
    isMultiLanguage,
    errors,
    isValid: errors.length === 0 && questions.length > 0,
  };
}

/**
 * Generate CSV template for question set import (legacy format)
 */
export function generateLegacyCsvTemplate(): string {
  const headers = LEGACY_HEADERS.join(',');
  const sampleRow = [
    '質問セット名',
    'どちらが、よりあなたにあてはまりますか？',
    '選択肢Aのテキスト',
    '選択肢Bのテキスト',
    'A',
    'B',
  ].join(',');

  return `${headers}\n${sampleRow}`;
}

/**
 * Generate multi-language CSV template for question set import (Requirement 49.8)
 */
export function generateMultiLanguageCsvTemplate(): string {
  const headers = MULTI_LANGUAGE_HEADERS.join(',');
  const sampleRow = [
    '質問セット名',
    'どちらが、よりあなたにあてはまりますか？',
    'Which one applies more to you?',
    'Mana yang lebih sesuai dengan Anda?',
    'Аль нь танд илүү тохирох вэ?',
    'कुन तपाईंलाई बढी लागू हुन्छ?',
    'Điều nào đúng với bạn hơn?',
    '選択肢Aのテキスト',
    'Choice A text',
    'Teks pilihan A',
    'А сонголтын текст',
    'विकल्प A पाठ',
    'Văn bản lựa chọn A',
    '選択肢Bのテキスト',
    'Choice B text',
    'Teks pilihan B',
    'Б сонголтын текст',
    'विकल्प B पाठ',
    'Văn bản lựa chọn B',
    'A',
    'B',
  ].join(',');

  return `${headers}\n${sampleRow}`;
}

/**
 * Download CSV template as file
 */
export function downloadCsvTemplate(multiLanguage: boolean = false): void {
  const template = multiLanguage ? generateMultiLanguageCsvTemplate() : generateLegacyCsvTemplate();
  const filename = multiLanguage ? 'question_set_multilang_template.csv' : 'question_set_template.csv';

  // Add BOM for Excel compatibility with Japanese characters
  const bom = '\uFEFF';
  const blob = new Blob([bom + template], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Count translations in parsed questions
 */
export function countTranslations(questions: ParsedQuestion[]): {
  totalQuestions: number;
  translatedQuestions: Record<SupportedLanguage, number>;
  totalChoices: number;
  translatedChoices: Record<SupportedLanguage, number>;
} {
  const result = {
    totalQuestions: questions.length,
    translatedQuestions: { en: 0, id: 0, mn: 0, ne: 0, vi: 0 },
    totalChoices: questions.length * 2,
    translatedChoices: { en: 0, id: 0, mn: 0, ne: 0, vi: 0 },
  };

  for (const q of questions) {
    if (q.textEn) result.translatedQuestions.en++;
    if (q.textId) result.translatedQuestions.id++;
    if (q.textMn) result.translatedQuestions.mn++;
    if (q.textNe) result.translatedQuestions.ne++;
    if (q.textVi) result.translatedQuestions.vi++;

    if (q.choiceA.textEn) result.translatedChoices.en++;
    if (q.choiceA.textId) result.translatedChoices.id++;
    if (q.choiceA.textMn) result.translatedChoices.mn++;
    if (q.choiceA.textNe) result.translatedChoices.ne++;
    if (q.choiceA.textVi) result.translatedChoices.vi++;

    if (q.choiceB.textEn) result.translatedChoices.en++;
    if (q.choiceB.textId) result.translatedChoices.id++;
    if (q.choiceB.textMn) result.translatedChoices.mn++;
    if (q.choiceB.textNe) result.translatedChoices.ne++;
    if (q.choiceB.textVi) result.translatedChoices.vi++;
  }

  return result;
}
